import type { EpochSeconds } from "./time";
import type { WorkDay } from "./workday";

/** The values of a project the selection needs. */
export interface ProjectOption {
  readonly id: string;
  readonly code: string;
  readonly name: string;
}

/** Favorites and recently used projects (spec 6.5.2 P-9). */
export interface ProjectChoices {
  readonly favorites: readonly string[];
  readonly recent: readonly string[];
}

/** A project switch as far as the current project is concerned. */
export interface SwitchLike {
  readonly kind: "Switch" | "Range";
  readonly projectId: string | null;
  readonly occurredAt: EpochSeconds | null;
  readonly createdAtMs: number;
  readonly syncStatus: "pending" | "synced" | "rejected";
  /** The activity of the switch (T-184). */
  readonly activityId?: string | null | undefined;
}

/** A block of the server's project view (`GET /me/project-days`). */
export interface ServerProjectBlock {
  readonly kind: "Work" | "Break";
  readonly start: EpochSeconds;
  readonly end: EpochSeconds;
  readonly projectId: string | null;
  readonly isOpen: boolean;
  /** The activity of the block (T-184). */
  readonly activityId?: string | null | undefined;
}

/** Number of recently used projects that are remembered. */
export const maxRecentProjects = 5;

/**
 * Orders projects for the selection: favorites in the order they were marked, then recently used
 * ones, then all others by code.
 *
 * @param projects - Selectable projects.
 * @param choices - Favorites and recently used projects.
 * @returns The ordered projects.
 */
export function orderProjects<T extends ProjectOption>(
  projects: readonly T[],
  choices: ProjectChoices,
): T[] {
  const byId = new Map(projects.map((project) => [project.id, project]));
  const first = [...choices.favorites, ...choices.recent]
    .filter((id, index, ids) => ids.indexOf(id) === index)
    .map((id) => byId.get(id))
    .filter((project): project is T => project !== undefined);
  const chosen = new Set(first.map((project) => project.id));
  const rest = projects
    .filter((project) => !chosen.has(project.id))
    .sort((left, right) => left.code.localeCompare(right.code, "de"));
  const ordered = [...first, ...rest];

  return ordered;
}

/**
 * Filters projects by a search text in code or name (case-insensitive).
 *
 * @param projects - Projects.
 * @param query - Search text; empty keeps all.
 * @returns The matching projects in their order.
 */
export function filterProjects<T extends ProjectOption>(
  projects: readonly T[],
  query: string,
): T[] {
  const needle = query.trim().toLocaleLowerCase("de");
  const matching = projects.filter(
    (project) =>
      needle === "" ||
      project.code.toLocaleLowerCase("de").includes(needle) ||
      project.name.toLocaleLowerCase("de").includes(needle),
  );

  return matching;
}

/**
 * Records a project as recently used.
 *
 * @param choices - Current choices.
 * @param projectId - The project.
 * @returns The choices with the project first in the recent list.
 */
export function rememberProject<T extends ProjectChoices>(choices: T, projectId: string): T {
  const recent = [projectId, ...choices.recent.filter((id) => id !== projectId)].slice(
    0,
    maxRecentProjects,
  );
  const updated: T = { ...choices, recent };

  return updated;
}

/**
 * Marks or unmarks a project as favorite.
 *
 * @param choices - Current choices.
 * @param projectId - The project.
 * @returns The changed choices.
 */
export function toggleFavorite<T extends ProjectChoices>(choices: T, projectId: string): T {
  const favorites = choices.favorites.includes(projectId)
    ? choices.favorites.filter((id) => id !== projectId)
    : [...choices.favorites, projectId];
  const updated: T = { ...choices, favorites };

  return updated;
}

/**
 * Returns the start of the shift that is still running, or `null` when nobody is clocked in.
 *
 * @param days - Working days of the local timeline.
 * @returns The start of the open shift.
 */
export function openShiftStart(days: readonly WorkDay[]): EpochSeconds | null {
  const last = days[days.length - 1];
  const shift =
    last === undefined || last.isClosed ? undefined : last.shifts[last.shifts.length - 1];
  const start = shift?.start ?? null;

  return start;
}

/** Input of {@link currentProjectId}. */
export interface CurrentProjectInput {
  /** Start of the running shift; `null` when clocked out. */
  readonly shiftStart: EpochSeconds | null;
  readonly now: EpochSeconds;
  /** Attributions recorded on this device. */
  readonly localSwitches: readonly SwitchLike[];
  /** Blocks of the server's project view of recent days. */
  readonly serverBlocks: readonly ServerProjectBlock[];
  /** Blocks of the local timeline with the live project of clock-in or break end. */
  readonly localBlocks: readonly {
    readonly kind: "Work" | "Break";
    readonly start: EpochSeconds;
    readonly projectId: string | null;
  }[];
}

/** The project and activity that apply now (T-184). */
export interface CurrentAttribution {
  readonly projectId: string | null;
  readonly activityId: string | null;
}

/**
 * Returns the project that applies now within the running shift (spec 6.5.3 for "now"): the later
 * of the last local switch of the shift and the block the server resolved last (a switch on
 * another device, or one the server has seen), otherwise the live project of the last clock-in or
 * break end. A local switch wins a tie: the server may not show a switch of the same second yet.
 *
 * @param input - Shift, local switches and server view.
 * @returns The project, or `null` if none applies or nobody is clocked in.
 */
export function currentProjectId(input: CurrentProjectInput): string | null {
  const projectId = currentAttribution(input).projectId;
  return projectId;
}

/**
 * Returns the project and the activity that apply now, decided like {@link currentProjectId}; the
 * live project of a clock-in carries no activity (T-184).
 *
 * @param input - Shift, local switches and server view.
 * @returns The project and activity; both `null` if nobody is clocked in.
 */
export function currentAttribution(input: CurrentProjectInput): CurrentAttribution {
  const shiftStart = input.shiftStart;

  if (shiftStart === null) {
    const none: CurrentAttribution = { projectId: null, activityId: null };
    return none;
  }

  const localSwitch = input.localSwitches
    .filter(
      (item) =>
        item.kind === "Switch" &&
        item.syncStatus !== "rejected" &&
        item.occurredAt !== null &&
        item.occurredAt >= shiftStart &&
        item.occurredAt <= input.now,
    )
    .sort(
      (left, right) =>
        (left.occurredAt ?? 0) - (right.occurredAt ?? 0) || left.createdAtMs - right.createdAtMs,
    )
    .at(-1);
  const serverBlock = input.serverBlocks
    .filter(
      (block) => block.kind === "Work" && block.start >= shiftStart && block.start <= input.now,
    )
    .at(-1);
  const localBlock = input.localBlocks
    .filter(
      (block) => block.kind === "Work" && block.start >= shiftStart && block.start <= input.now,
    )
    .at(-1);
  const serverIsNewer =
    serverBlock !== undefined &&
    (localSwitch === undefined || serverBlock.start > (localSwitch.occurredAt ?? 0));
  const current: CurrentAttribution =
    localSwitch === undefined && serverBlock === undefined
      ? { projectId: localBlock?.projectId ?? null, activityId: null }
      : serverIsNewer
        ? { projectId: serverBlock.projectId, activityId: serverBlock.activityId ?? null }
        : {
            projectId: localSwitch?.projectId ?? null,
            activityId: localSwitch?.activityId ?? null,
          };

  return current;
}
